import { useState, useCallback, useRef } from 'react';
import { FileWithPreview } from '../FileUploadConfiguration.types';
import {
  generateFileId,
  validateFileSize,
  isFileTypeAllowed,
  canPreviewFile,
} from '../utils/fileUtils';
import { fileUploadConfig } from '../config/fileUploadConfig';

export interface UseFileManagerOptions {
  allowMultiple?: boolean;
  maxFileSize?: number;
  maxFiles?: number;
  allowedTypes?: string[];
  blockedExtensions?: string[];
  autoPreview?: boolean;
  /** If true, excluded files (blocked/over-limit) will be added to files list with markers */
  trackExcludedFiles?: boolean;
}

export interface FileValidationError {
  type: 'max_files' | 'file_type' | 'file_size';
  message: string;
  fileName?: string;
}

export interface AddFilesResult {
  errors: FileValidationError[];
  /** Files that were successfully added (will be uploaded) */
  addedFiles: FileWithPreview[];
  /** Files that were excluded due to blocked extension or over-limit */
  excludedFiles: FileWithPreview[];
}

export interface UseFileManagerResult {
  files: FileWithPreview[];
  /** Returns all files including those marked as excluded (for display purposes) */
  allFilesForDisplay: FileWithPreview[];
  addFiles: (files: FileList | File[]) => Promise<AddFilesResult>;
  removeFile: (fileId: string) => void;
  clearFiles: () => void;
  updateFileProgress: (fileId: string, progress: number, type: 'upload' | 'backend') => void;
  updateFileStatus: (fileId: string, status: FileWithPreview['status']) => void;
  maxFiles: number;
  remainingSlots: number;
  /** Get only uploadable files (excludes blocked and over-limit files) */
  getUploadableFiles: () => FileWithPreview[];
}

/**
 * Hook for managing multiple file uploads
 */
export function useFileManager(options: UseFileManagerOptions = {}): UseFileManagerResult {
  const {
    allowMultiple = true,
    maxFileSize,
    maxFiles = fileUploadConfig.maxFiles,
    allowedTypes,
    blockedExtensions = fileUploadConfig.blockedExtensions,
    autoPreview = true,
    trackExcludedFiles = true,
  } = options;

  const [files, setFiles] = useState<FileWithPreview[]>([]);
  const filePreviewCache = useRef<Map<string, string>>(new Map());

  // Calculate remaining upload slots (only count uploadable files)
  const uploadableCount = files.filter((f) => !f.isBlocked && !f.isOverLimit).length;
  const remainingSlots = allowMultiple
    ? Math.max(0, maxFiles - uploadableCount)
    : uploadableCount === 0
      ? 1
      : 0;

  // Get all files for display (including excluded ones)
  const allFilesForDisplay = files;

  // Get only uploadable files
  const getUploadableFiles = useCallback(() => {
    return files.filter((f) => !f.isBlocked && !f.isOverLimit);
  }, [files]);

  const addFiles = useCallback(
    async (newFiles: FileList | File[]): Promise<AddFilesResult> => {
      const fileArray = Array.from(newFiles);
      const validFiles: FileWithPreview[] = [];
      const excludedFiles: FileWithPreview[] = [];
      const errors: FileValidationError[] = [];

      // Count current uploadable files
      const currentUploadableCount = files.filter((f) => !f.isBlocked && !f.isOverLimit).length;
      const availableSlots = allowMultiple
        ? maxFiles - currentUploadableCount
        : currentUploadableCount === 0
          ? 1
          : 0;

      // Check if we're already at the limit
      if (allowMultiple && availableSlots <= 0) {
        errors.push({
          type: 'max_files',
          message: `Maximum of ${maxFiles} files allowed. Please remove some files before adding more.`,
        });
        return { errors, addedFiles: [], excludedFiles: [] };
      }

      let addedCount = 0;

      // Track blocked extensions and their file counts for consolidated error messages
      const blockedExtensionCounts: Record<string, string[]> = {};
      const oversizedFiles: string[] = [];
      const duplicateFiles: string[] = [];
      const duplicateExcludedFiles: string[] = [];

      for (const file of fileArray) {
        const fileId = generateFileId(file);

        // Check if file already exists
        const existingFile = files.find((f) => f.id === fileId);
        if (existingFile) {
          // Track duplicates separately for blocked vs normal files
          if (existingFile.isBlocked || existingFile.isOverLimit) {
            duplicateExcludedFiles.push(file.name);
          } else {
            duplicateFiles.push(file.name);
          }
          continue;
        }

        // Create base FileWithPreview object
        const fileWithPreview = Object.assign(file, {
          id: fileId,
          status: 'pending' as const,
          uploadProgress: 0,
          backendUploadProgress: 0,
          isBlocked: false,
          isOverLimit: false,
          exclusionReason: undefined,
        }) as FileWithPreview;

        // Check if file extension is blocked
        let isExcluded = false;
        if (blockedExtensions && blockedExtensions.length > 0) {
          const fileExtension = file.name.includes('.')
            ? `.${file.name.split('.').pop()?.toLowerCase()}`
            : '';
          if (blockedExtensions.includes(fileExtension)) {
            fileWithPreview.isBlocked = true;
            fileWithPreview.exclusionReason = `File type "${fileExtension}" is not allowed`;
            isExcluded = true;
            // Track for consolidated error message
            if (!blockedExtensionCounts[fileExtension]) {
              blockedExtensionCounts[fileExtension] = [];
            }
            blockedExtensionCounts[fileExtension].push(file.name);
          }
        }

        // Validate file size
        if (!isExcluded) {
          const sizeValidation = validateFileSize(file, maxFileSize);
          if (!sizeValidation.valid) {
            fileWithPreview.isBlocked = true;
            fileWithPreview.exclusionReason = sizeValidation.error || 'File size exceeds limit';
            isExcluded = true;
            oversizedFiles.push(file.name);
          }
        }

        // Validate file type (if allowedTypes is specified)
        if (!isExcluded && !isFileTypeAllowed(file, allowedTypes)) {
          const allowedDisplay = allowedTypes?.join(', ') || 'all types';
          fileWithPreview.isBlocked = true;
          fileWithPreview.exclusionReason = `File type not allowed. Allowed types: ${allowedDisplay}`;
          isExcluded = true;
          errors.push({
            type: 'file_type',
            message: `File type not allowed. Allowed types: ${allowedDisplay}`,
            fileName: file.name,
          });
        }

        // Check if this file would exceed the limit
        if (!isExcluded) {
          if (allowMultiple && addedCount >= availableSlots) {
            fileWithPreview.isOverLimit = true;
            fileWithPreview.exclusionReason = `Exceeds maximum of ${maxFiles} files`;
            isExcluded = true;
          } else if (!allowMultiple && addedCount >= 1) {
            // When allowMultiple is false, only allow one file
            fileWithPreview.isOverLimit = true;
            fileWithPreview.exclusionReason = 'Only one file allowed';
            isExcluded = true;
          }
        }

        // Generate preview if enabled
        if (autoPreview && canPreviewFile(file)) {
          const cachedPreview = filePreviewCache.current.get(fileId);
          if (cachedPreview) {
            fileWithPreview.preview = cachedPreview;
          } else {
            try {
              // For text-based files, generate preview immediately
              // For other files (PDF, Excel, Word, Images), preview will be generated on-demand
              const isTextFile =
                file.type.startsWith('text/') ||
                file.type === 'application/json' ||
                file.name.endsWith('.csv') ||
                file.name.endsWith('.json') ||
                file.name.endsWith('.txt') ||
                file.name.endsWith('.xml');

              if (isTextFile) {
                const preview = await generateFilePreview(file);
                if (preview) {
                  fileWithPreview.preview = preview;
                  filePreviewCache.current.set(fileId, preview);
                }
              } else {
                // Mark as previewable but don't generate yet (will be done on-demand)
                fileWithPreview.preview = 'previewable';
              }
            } catch (error) {
              console.error('Error generating preview:', error);
              fileWithPreview.previewError = 'Preview not available';
            }
          }
        } else if (autoPreview) {
          fileWithPreview.previewError = 'Preview not available for this file type';
        }

        if (isExcluded) {
          excludedFiles.push(fileWithPreview);
        } else {
          validFiles.push(fileWithPreview);
          addedCount++;
        }
      }

      // Generate consolidated error messages

      // Blocked extension errors - group by extension type
      for (const [extension, fileNames] of Object.entries(blockedExtensionCounts)) {
        if (fileNames.length === 1) {
          errors.push({
            type: 'file_type',
            message: `File type "${extension}" is not allowed`,
            fileName: fileNames[0],
          });
        } else {
          errors.push({
            type: 'file_type',
            message: `${fileNames.length} files with type "${extension}" are not allowed`,
          });
        }
      }

      // Oversized files error
      if (oversizedFiles.length === 1) {
        errors.push({
          type: 'file_size',
          message: 'File size exceeds limit',
          fileName: oversizedFiles[0],
        });
      } else if (oversizedFiles.length > 1) {
        errors.push({
          type: 'file_size',
          message: `${oversizedFiles.length} files exceed the size limit`,
        });
      }

      // Over limit error (consolidated)
      const overLimitCount = excludedFiles.filter((f) => f.isOverLimit).length;
      if (overLimitCount > 0) {
        errors.push({
          type: 'max_files',
          message: `${overLimitCount} file${overLimitCount > 1 ? 's' : ''} exceed${overLimitCount === 1 ? 's' : ''} the maximum limit of ${maxFiles} files`,
        });
      }

      // Duplicate file warnings
      if (duplicateFiles.length > 0) {
        errors.push({
          type: 'file_type',
          message:
            duplicateFiles.length === 1
              ? `"${duplicateFiles[0]}" is already selected`
              : `${duplicateFiles.length} files are already selected`,
        });
      }

      // Duplicate excluded file warnings (files that were already blocked)
      if (duplicateExcludedFiles.length > 0) {
        errors.push({
          type: 'file_type',
          message:
            duplicateExcludedFiles.length === 1
              ? `"${duplicateExcludedFiles[0]}" was already added (blocked)`
              : `${duplicateExcludedFiles.length} blocked files were already added`,
        });
      }

      // Add both valid and excluded files to state (excluded files are shown with visual indicators)
      const allNewFiles = trackExcludedFiles ? [...validFiles, ...excludedFiles] : validFiles;
      if (allNewFiles.length > 0) {
        setFiles((prev) => (allowMultiple ? [...prev, ...allNewFiles] : allNewFiles.slice(0, 1)));
      }

      return { errors, addedFiles: validFiles, excludedFiles };
    },
    [
      files,
      allowMultiple,
      maxFiles,
      maxFileSize,
      allowedTypes,
      blockedExtensions,
      autoPreview,
      trackExcludedFiles,
    ],
  );

  const removeFile = useCallback((fileId: string) => {
    setFiles((prev) => prev.filter((f) => f.id !== fileId));
    filePreviewCache.current.delete(fileId);
  }, []);

  const clearFiles = useCallback(() => {
    setFiles([]);
    filePreviewCache.current.clear();
  }, []);

  const updateFileProgress = useCallback(
    (fileId: string, progress: number, type: 'upload' | 'backend') => {
      setFiles((prev) =>
        prev.map((file) => {
          if (file.id === fileId) {
            return {
              ...file,
              [type === 'upload' ? 'uploadProgress' : 'backendUploadProgress']: progress,
              status: progress === 100 ? ('uploaded' as const) : ('uploading' as const),
            };
          }
          return file;
        }),
      );
    },
    [],
  );

  const updateFileStatus = useCallback((fileId: string, status: FileWithPreview['status']) => {
    setFiles((prev) =>
      prev.map((file) => {
        if (file.id === fileId) {
          return { ...file, status };
        }
        return file;
      }),
    );
  }, []);

  return {
    files,
    allFilesForDisplay,
    addFiles,
    removeFile,
    clearFiles,
    updateFileProgress,
    updateFileStatus,
    maxFiles,
    remainingSlots,
    getUploadableFiles,
  };
}

/**
 * Helper function to generate file preview
 */
async function generateFilePreview(file: File): Promise<string | null> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();

    reader.onload = (e) => {
      const result = e.target?.result;
      if (typeof result === 'string') {
        resolve(result);
      } else {
        reject(new Error('Failed to read file content'));
      }
    };

    reader.onerror = () => {
      reject(new Error('Error reading file'));
    };

    // Read as text for previewable files
    reader.readAsText(file);
  });
}
